/**
 * ONWARD CHECKOUT+ — cart drawer gate.
 *
 * The server render in snippets/runit-checkout-plus.liquid is the source of
 * truth: it withholds the whole block when the cart does not qualify, so an
 * ineligible cart carries no Checkout+ markup at all. This file repeats that
 * decision on the client so the state can change inside the drawer without a
 * page reload, and so a provider that mounts into the slot is told to tear its
 * widget down on the way out rather than being hidden mid-flow.
 *
 * Provider contract (used by the Onward embed / a thin adapter):
 *   node        [data-runit-cp-mount]
 *   attributes  data-runit-cp-eligible | -reason | -subtotal | -threshold
 *               | -armoured-lines | -armoured-keys
 *   events      runit:checkout-plus:state   { eligible, reason, subtotal, threshold, armouredLines }
 *               runit:checkout-plus:mount   { node }
 *               runit:checkout-plus:unmount { node, reason }
 *   api         window.RunitCheckoutPlus.refresh() / .isEligible() / .state()
 */
(function () {
  'use strict';

  var WRAPPER = '[data-runit-checkout-plus]';
  var SLOT = '[data-runit-cp-mount]';
  var DRAWER = 'cart-drawer';
  var FLAG = 'runitCpReady';
  var EVENTS = {
    state: 'runit:checkout-plus:state',
    mount: 'runit:checkout-plus:mount',
    unmount: 'runit:checkout-plus:unmount'
  };

  var observer = null;
  var frame = 0;
  var current = { eligible: false, reason: 'absent', subtotal: 0, threshold: 0, armouredLines: 0 };
  var lastKey = '';

  function int(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  function stateOf(root) {
    return {
      eligible: root.getAttribute('data-runit-cp-eligible') === 'true',
      reason: root.getAttribute('data-runit-cp-reason') || 'none',
      subtotal: int(root.getAttribute('data-runit-cp-subtotal')),
      threshold: int(root.getAttribute('data-runit-cp-threshold')),
      armouredLines: int(root.getAttribute('data-runit-cp-armoured-lines')),
      armouredKeys: (root.getAttribute('data-runit-cp-armoured-keys') || '').split(',').filter(Boolean),
      preview: root.getAttribute('data-runit-cp-preview') === 'true'
    };
  }

  function cartHasOnwardEligibleProduct(state) {
    return state.armouredLines > 0;
  }

  function isCheckoutPlusEligible(state) {
    return state.eligible === true;
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function track(name, detail) {
    if (!Array.isArray(window.dataLayer)) return;
    var payload = { event: name };
    for (var key in detail) if (detail.hasOwnProperty(key)) payload[key] = detail[key];
    window.dataLayer.push(payload);
  }

  /* Apply one wrapper's state. Returns whether a real provider is mounted. */
  function updateCheckoutPlusVisibility(root, state) {
    var slot = root.querySelector(SLOT);
    var mounted = !!(slot && slot.children.length > 0 && !state.preview);

    if (!isCheckoutPlusEligible(state)) {
      if (!root.hidden) root.hidden = true;
      if (root.dataset.runitCpMounted === 'true') {
        root.dataset.runitCpMounted = 'false';
        emit(EVENTS.unmount, { node: root, reason: state.reason, state: state });
      }
      root.classList.remove('is-mounted');
      return false;
    }

    if (root.hidden) root.hidden = false;

    if (mounted && root.dataset.runitCpMounted !== 'true') {
      root.dataset.runitCpMounted = 'true';
      emit(EVENTS.mount, { node: root, state: state });
    } else if (!mounted && root.dataset.runitCpMounted === 'true') {
      root.dataset.runitCpMounted = 'false';
      emit(EVENTS.unmount, { node: root, reason: 'provider-absent', state: state });
    }

    root.classList.toggle('is-mounted', mounted);
    return mounted;
  }

  function wrappers() {
    var scope = document.querySelector(DRAWER) || document;
    return scope.querySelectorAll(WRAPPER);
  }

  function sync(cause) {
    var roots = wrappers();
    if (roots.length) {
      for (var i = 0; i < roots.length; i++) {
        var state = stateOf(roots[i]);
        updateCheckoutPlusVisibility(roots[i], state);
        if (i === 0) current = state;
      }
    } else {
      current = { eligible: false, reason: 'absent', subtotal: 0, threshold: 0, armouredLines: 0 };
    }
    current.cause = cause || 'sync';

    var key = [current.eligible, current.reason, current.subtotal, current.armouredLines, roots.length].join('|');
    if (key !== lastKey) {
      lastKey = key;
      emit(EVENTS.state, current);
      track(current.eligible ? 'runit_checkout_plus_shown' : 'runit_checkout_plus_hidden', {
        reason: current.reason,
        subtotal: current.subtotal / 100,
        threshold: current.threshold / 100,
        armoured_lines: current.armouredLines
      });
    }
    return current;
  }

  function schedule(cause) {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = 0;
      sync(cause);
    });
  }

  /* The drawer re-renders its own subtree from the Section Rendering API on
     every cart change, which replaces this block along with the rest of the
     markup. Watching that one element keeps the gate honest without observing
     the whole document. */
  function observeDrawer() {
    var drawer = document.querySelector(DRAWER);
    if (!drawer || observer) return;
    observer = new MutationObserver(function () { schedule('drawer-mutation'); });
    observer.observe(drawer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-runit-cp-eligible', 'data-runit-cp-reason', 'data-runit-cp-subtotal',
                        'data-runit-cp-armoured-lines', 'data-runit-cp-armoured-keys']
    });
  }

  function init() {
    if (document.documentElement.dataset[FLAG] === 'true') return;
    document.documentElement.dataset[FLAG] = 'true';  // data-runit-cp-ready

    observeDrawer();
    /* Fired by the theme's own cart code after every quantity change. */
    document.addEventListener('cartQuantityUpdated', function () { schedule('cart-quantity-updated'); });
    document.addEventListener('shopify:section:load', function () {
      if (!observer) observeDrawer();
      schedule('section-load');
    });
    window.addEventListener('load', function () { schedule('load'); });
    sync('init');
  }

  window.RunitCheckoutPlus = {
    refresh: function () { return sync('api'); },
    isEligible: function () { return isCheckoutPlusEligible(current); },
    cartHasOnwardEligibleProduct: function () { return cartHasOnwardEligibleProduct(current); },
    state: function () { return current; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
